import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  const bootstrap = useCallback(async () => {
    // CRITICAL: If returning from Google OAuth, skip the /me check —
    // AuthCallback exchanges the session_id and establishes auth first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("olympus_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setImpersonating(!!sessionStorage.getItem("olympus_admin_token"));
    } catch {
      localStorage.removeItem("olympus_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("olympus_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("olympus_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("olympus_token");
    setUser(null);
  };

  const oauthLogin = (token, userData) => {
    localStorage.setItem("olympus_token", token);
    setUser(userData);
  };

  const impersonate = (token, userData) => {
    sessionStorage.setItem("olympus_admin_token", localStorage.getItem("olympus_token"));
    localStorage.setItem("olympus_token", token);
    setUser(userData);
    setImpersonating(true);
  };

  const exitImpersonation = () => {
    const adminToken = sessionStorage.getItem("olympus_admin_token");
    if (adminToken) {
      localStorage.setItem("olympus_token", adminToken);
      sessionStorage.removeItem("olympus_admin_token");
    }
    setImpersonating(false);
    bootstrap();
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, oauthLogin, impersonate, exitImpersonation, impersonating, refresh: bootstrap }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
